<template>
  <Component
    :is="fileComponent"
    class="row c-file-upload-attachment flex items-center justify-between"
    v-bind="fileProps"
  >
    <div class="col-8 flex items-center">
      <EllipseContent class="c-file-upload-attachment__filename q-mr-sm">
        {{ fileName }}
      </EllipseContent>
      <span>{{ prettyFileSize }}</span>
    </div>

    <div class="col-4 flex items-center justify-end">
      <ProgressBar
        v-if="uploading"
        class="flex-1"
        :progress="attachment.progress"
        :total="attachment.total"
      />

      <BaseButton
        v-if="!hideRemoveButton"
        flat
        icon="ion-close"
        :new-design="newDesign"
        padding="xs"
        size="sm"
        @click.prevent="removeFile"
      />
    </div>
  </Component>
</template>

<script>
import {
  BaseButton,
  EllipseContent,
  ProgressBar,
} from "shared/components/base";

export default {
  name: "FileUploadAttachment",
  components: {
    BaseButton,
    EllipseContent,
    ProgressBar,
  },
  props: {
    attachment: {
      type: Object,
      required: true,
    },
    newDesign: {
      type: Boolean,
    },
    downloadable: {
      type: Boolean,
    },
    hideRemoveButton: {
      type: Boolean,
    },
  },
  emits: ["remove", "cancel"],
  computed: {
    fileName() {
      return this.attachment.activeStorageBlob
        ? this.attachment.activeStorageBlob.filename
        : this.attachment.name;
    },
    fileSize() {
      return this.attachment.activeStorageBlob
        ? this.attachment.activeStorageBlob.byteSize
        : this.attachment.total;
    },
    prettyFileSize() {
      const index = Math.floor(Math.log(this.fileSize) / Math.log(1024));
      const unit = ["B", "kB", "MB", "GB", "TB"][index];
      const size = Number((this.fileSize / 1024 ** index).toFixed(2));

      return this.newDesign ? `${size} ${unit}` : `(${size} ${unit})`;
    },
    uploading() {
      return (
        !this.attachment.activeStorageBlob &&
        this.attachment.progress < this.attachment.total
      );
    },
    canDownload() {
      return this.downloadable && this.attachment.activeStorageBlob;
    },
    fileComponent() {
      return this.canDownload ? "a" : "span";
    },
    fileProps() {
      const classes = {
        "new-design": this.newDesign,
        downloadable: this.downloadable,
      };

      if (this.canDownload) {
        const fileAttrs = this.attachment.blobAttributes();

        return {
          class: classes,
          href: fileAttrs.url,
          target: "_blank",
          rel: "noopener noreferrer",
        };
      }

      return {
        class: classes,
      };
    },
  },
  methods: {
    removeFile() {
      if (this.attachment.activeStorageBlob) {
        this.$emit("remove");
      } else {
        this.$emit("cancel");
      }
    },
  },
};
</script>

<style lang="scss" scoped>
.c-file-upload-attachment {
  padding: 5px 10px;
  background: $silver-white;

  &__filename {
    max-width: 70%;
  }

  &.new-design {
    background: none;

    &__filename,
    &__filesize {
      justify-content: space-between;
    }
  }

  &.downloadable {
    cursor: pointer;

    &:hover {
      background: var(--s-color-denim-1);
    }
  }
}
</style>
